import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { invalidateFinancials } from '../../lib/invalidate';
import { trpc } from '../../lib/trpc';

type Props = {
  transactionId: string;
  categoryId: string | null;
  categorySource: string | null;
  /** False for rows that never get a rule (incoming Swish is sorted by hand) */
  ruleAllowed?: boolean;
};

/** Compact inline category select for dense lists (insights, settings dialogs). */
export function TransactionCategoryCell({
  transactionId,
  categoryId,
  categorySource,
  ruleAllowed = true,
}: Props) {
  const utils = trpc.useUtils();
  const groupsQuery = trpc.category.listGroups.useQuery();

  const createRuleMutation = trpc.transaction.categorize.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.categoryRule.list.invalidate(),
        utils.categoryRule.findDuplicateGroups.invalidate(),
      ]);
      toast.success('Regel skapad');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const categorizeMutation = trpc.transaction.categorize.useMutation({
    onSuccess: async (_data, variables) => {
      await invalidateFinancials(utils);
      await utils.category.transactions.invalidate();
      if (!ruleAllowed) {
        toast.success('Kategori ändrad');
        return;
      }
      toast.success('Kategori ändrad', {
        action: {
          label: 'Skapa regel',
          onClick: () => {
            createRuleMutation.mutate({
              transactionId: variables.transactionId,
              categoryId: variables.categoryId,
              createRule: true,
            });
          },
        },
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const groups = groupsQuery.data ?? [];
  const currentValue = categoryId ?? '__none__';

  return (
    <div className="flex items-center gap-1.5">
      <Select
        value={currentValue}
        onValueChange={(value) => {
          if (value === '__none__' || value === currentValue) return;
          categorizeMutation.mutate({
            transactionId,
            categoryId: value,
            createRule: false,
          });
        }}
        disabled={categorizeMutation.isPending}
      >
        <SelectTrigger
          size="sm"
          className={
            categoryId
              ? 'h-7 w-[180px] text-xs'
              : 'h-7 w-[180px] border-dashed border-warning/50 bg-warning-soft text-xs text-warning'
          }
        >
          <SelectValue placeholder="Välj kategori…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__" disabled>
            Välj kategori…
          </SelectItem>
          {groups.map((group, index) => (
            <SelectGroup key={group.id}>
              {index > 0 && <SelectSeparator />}
              <SelectLabel>{group.name}</SelectLabel>
              {group.categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      {categorySource === 'manual' && (
        <span className="inline-flex h-5 shrink-0 items-center rounded-md bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
          Manuell
        </span>
      )}
    </div>
  );
}
